<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div class="row justify-content-center">
      <div class="col-sm-12 col-xs-12">
        <div class="pb-0 col-sm-12 d-flex mainDiv">
          <div class="col-sm-12 col-xs-12">
            <!-- 타이틀 영역 -->
            <div
              class="col-sm-12 col-xs-12 titleDiv CertListTitle justify-content-between title-area"
            >
              <div>
                <h3>구독 상품 관리</h3>
                <h5>시스템 제품을 조합하여 구독 상품을 생성하고 관리합니다.</h5>
              </div>
              <div class="title-actions">
                <a-button
                  type="primary"
                  class="yellow-button"
                  @click="openCreateModal"
                >
                  <i class="fa-solid fa-plus" />
                  상품 추가
                </a-button>
              </div>
            </div>

            <!-- 필터 영역 -->
            <div class="filter-area">
              <a-select
                v-model:value="filters.type"
                placeholder="상품 타입"
                allowClear
                style="width: 150px"
                @change="fetchPackages"
              >
                <a-select-option value="general">기본</a-select-option>
                <a-select-option value="reseller">리셀러</a-select-option>
                <a-select-option value="custom">커스텀</a-select-option>
              </a-select>
            </div>

            <!-- 테이블 -->
            <div class="table-container">
              <a-table
                :columns="columns"
                :data-source="packages"
                :loading="loading"
                :pagination="false"
                row-key="id"
              >
                <!-- 상품명 -->
                <template #name="{ record }">
                  <span class="package-name">{{ record.name }}</span>
                </template>

                <!-- 타입 -->
                <template #type="{ record }">
                  <div class="type-cell">
                    <a-tag :color="getTypeColor(record.type)">
                      {{ getTypeLabel(record.type) }}
                    </a-tag>
                    <div v-if="record.reseller" class="reseller-name">
                      {{ record.reseller.name }}
                    </div>
                  </div>
                </template>

                <!-- 구성 제품 -->
                <template #items="{ record }">
                  <div class="items-list">
                    <a-tag
                      v-for="productType in record.items"
                      :key="productType"
                      class="item-tag"
                    >
                      <i :class="['fa-solid', getProductIcon(productType)]" />
                      {{ getProductLabel(productType) }}
                    </a-tag>
                  </div>
                </template>

                <!-- 과금 방식 -->
                <template #billingType="{ record }">
                  <a-tag color="green">{{
                    getBillingTypeLabel(record.billingType)
                  }}</a-tag>
                </template>

                <!-- 설치비 -->
                <template #installationFee="{ record }">
                  <div class="price-cell">
                    <span class="final-price">{{
                      formatCurrency(record.pricing?.finalInstallationFee)
                    }}</span>
                    <span
                      v-if="record.pricing?.installationDiscountRate > 0"
                      class="discount-rate"
                    >
                      ({{ record.pricing.installationDiscountRate }}% 할인)
                    </span>
                  </div>
                </template>

                <!-- 사용료 -->
                <template #usageFee="{ record }">
                  <div class="price-cell">
                    <span class="final-price">{{
                      formatCurrency(record.pricing?.finalUsageFee)
                    }}</span>
                    <span
                      v-if="record.pricing?.usageDiscountRate > 0"
                      class="discount-rate"
                    >
                      ({{ record.pricing.usageDiscountRate }}% 할인)
                    </span>
                  </div>
                </template>

                <!-- 상태 -->
                <template #isActive="{ record }">
                  <a-tag :color="record.isActive ? 'success' : 'default'">
                    {{ record.isActive ? "활성" : "비활성" }}
                  </a-tag>
                </template>

                <!-- 액션 -->
                <template #action="{ record }">
                  <a-space>
                    <a-button size="small" @click="openEditModal(record)">
                      <i class="fa-regular fa-pen-to-square" />
                    </a-button>
                    <a-popconfirm
                      title="이 상품을 삭제하시겠습니까?"
                      ok-text="삭제"
                      cancel-text="취소"
                      @confirm="handleDelete(record.id)"
                    >
                      <a-button size="small" danger>
                        <i class="fa-regular fa-trash-can" />
                      </a-button>
                    </a-popconfirm>
                  </a-space>
                </template>
              </a-table>
            </div>

            <!-- 데이터 없음 -->
            <div v-if="!loading && packages.length === 0" class="empty-state">
              <i class="fa-solid fa-box-open" />
              <p>등록된 구독 상품이 없습니다.</p>
              <a-button
                type="primary"
                class="yellow-button"
                @click="openCreateModal"
              >
                첫 상품 추가하기
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 생성/수정 모달 -->
    <UpsertPackageModal
      :visible="modal.visible"
      :package-data="modal.packageData"
      :system-products="systemProducts"
      :resellers="resellers"
      :custom-companies="customCompanies"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import { message } from "ant-design-vue";
import UpsertPackageModal from "./components/UpsertPackageModal.vue";
import { subscriptionPackageApi, systemProductApi } from "./api";
import {
  SubscriptionPackageTypeLabel,
  SubscriptionPackageTypeColor,
  BillingTypeLabel,
  SystemProductTypeLabel,
  SystemProductTypeIcon,
} from "./enums";

export default {
  name: "SubscriptionPackageList",
  components: {
    UpsertPackageModal,
  },
  data() {
    return {
      loading: false,
      packages: [],
      systemProducts: [],
      resellers: [],
      customCompanies: [],
      filters: {
        type: undefined,
      },
      modal: {
        visible: false,
        packageData: null,
      },
      columns: [
        {
          title: "상품명",
          dataIndex: "name",
          key: "name",
          slots: { customRender: "name" },
          width: 200,
        },
        {
          title: "타입",
          dataIndex: "type",
          key: "type",
          slots: { customRender: "type" },
          width: 150,
        },
        {
          title: "구성 제품",
          dataIndex: "items",
          key: "items",
          slots: { customRender: "items" },
          width: 280,
        },
        {
          title: "과금",
          dataIndex: "billingType",
          key: "billingType",
          slots: { customRender: "billingType" },
          width: 80,
        },
        {
          title: "설치비",
          dataIndex: "installationFee",
          key: "installationFee",
          slots: { customRender: "installationFee" },
          width: 150,
        },
        {
          title: "사용료",
          dataIndex: "usageFee",
          key: "usageFee",
          slots: { customRender: "usageFee" },
          width: 150,
        },
        {
          title: "상태",
          dataIndex: "isActive",
          key: "isActive",
          slots: { customRender: "isActive" },
          width: 80,
        },
        {
          title: "",
          key: "action",
          slots: { customRender: "action" },
          width: 100,
          fixed: "right",
        },
      ],
    };
  },
  mounted() {
    this.fetchPackages();
    this.fetchSystemProducts();
    this.fetchResellers();
    this.fetchCustomCompanies();
  },
  methods: {
    async fetchSystemProducts() {
      try {
        this.systemProducts = await systemProductApi.getAll();
      } catch (error) {
        console.error("시스템 제품 목록 조회 실패:", error);
      }
    },
    async fetchResellers() {
      try {
        this.resellers = await subscriptionPackageApi.getResellers();
      } catch (error) {
        console.error("리셀러 목록 조회 실패:", error);
      }
    },
    async fetchCustomCompanies() {
      try {
        this.customCompanies =
          await subscriptionPackageApi.getCustomCompanies();
      } catch (error) {
        console.error("병원/제조사 목록 조회 실패:", error);
      }
    },
    async fetchPackages() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.type) params.type = this.filters.type;
        this.packages = await subscriptionPackageApi.getAll(params);
      } catch (error) {
        console.error("상품 목록 조회 실패:", error);
        message.error("상품 목록을 불러오는데 실패했습니다.");
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.modal.packageData = null;
      this.modal.visible = true;
    },
    openEditModal(pkg) {
      this.modal.packageData = pkg;
      this.modal.visible = true;
    },
    closeModal() {
      this.modal.visible = false;
      this.modal.packageData = null;
    },
    handleSaved() {
      this.fetchPackages();
    },
    async handleDelete(id) {
      try {
        await subscriptionPackageApi.delete(id);
        message.success("상품이 삭제되었습니다.");
        this.fetchPackages();
      } catch (error) {
        console.error("상품 삭제 실패:", error);
        message.error("상품 삭제에 실패했습니다.");
      }
    },
    getTypeLabel(type) {
      return SubscriptionPackageTypeLabel[type] || type;
    },
    getTypeColor(type) {
      return SubscriptionPackageTypeColor[type] || "default";
    },
    getBillingTypeLabel(type) {
      return BillingTypeLabel[type] || type;
    },
    getProductLabel(type) {
      return SystemProductTypeLabel[type] || type;
    },
    getProductIcon(type) {
      return SystemProductTypeIcon[type] || "fa-box";
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return "₩ 0";
      return `₩ ${Number(value).toLocaleString()}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.row {
  padding: 28px 28px 72px 28px !important;
}

.title-area {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;

  h3 {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    color: #17191c;
  }

  h5 {
    margin: 8px 0 0;
    font-size: 14px;
    font-weight: 400;
    color: #666;
  }
}

.title-actions {
  display: flex;
  gap: 8px;
}

.filter-area {
  margin-bottom: 16px;
  display: flex;
  gap: 12px;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.package-name {
  font-weight: 600;
  color: #17191c;
}

.type-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;

  :deep(.ant-tag) {
    margin: 0;
    padding: 0 6px;
    font-size: 12px;
    line-height: 20px;
  }
}

.reseller-name {
  color: #666;
  font-size: 12px;
}

.items-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.item-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 11px;
  }
}

.price-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.final-price {
  font-weight: 600;
  color: #17191c;
}

.discount-rate {
  font-size: 11px;
  color: #f5222d;
}

.yellow-button {
  background-color: #ffe414 !important;
  border-color: #ffe414 !important;
  color: #17191c !important;
  font-weight: 600;

  &:hover {
    background-color: #ffd900 !important;
    border-color: #ffd900 !important;
  }

  i {
    margin-right: 6px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #999;
  background: #fff;
  border-radius: 8px;
  margin-top: 16px;

  i {
    font-size: 48px;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    margin: 0 0 16px;
  }
}
</style>
